import { AlertCircle, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface FormErrorGroupProps {
  errors: FieldErrors;
  isSubmitted?: boolean;
}

const ErrorContainer = styled.div<{ $isVisible: boolean }>`
  margin-bottom: 16px;
  background-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fecaca')};
  border-radius: 6px;
  padding: 14px 16px;
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: flex-start;
  gap: 12px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#ef4444')};
  margin-top: 2px;
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const ErrorTitle = styled.div`
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fca5a5' : '#991b1b')};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ErrorItem = styled.li`
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#dc2626')};
  font-size: 13px;
  line-height: 1.5;
  padding-left: 16px;
  position: relative;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &::before {
    content: '•';
    position: absolute;
    left: 4px;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#ef4444')};
  }
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#dc2626')};
  opacity: 0.6;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: none;
    opacity: 1;
  }
`;

const GENERIC_MESSAGES = ['required', 'invalid', 'expected', 'too_small', 'too_big'];

const isGenericMessage = (msg: string): boolean => {
  const lower = msg.toLowerCase();
  return GENERIC_MESSAGES.includes(lower)
    || lower.length < 3
    || /^[a-z_]+$/.test(lower);
};

const extractMessages = (obj: FieldErrors): string[] => {
  const messages: string[] = [];

  const processError = (error: unknown, fieldPath?: string): void => {
    if (!error || typeof error !== 'object') return;

    const err = error as Record<string, unknown>;

    if (typeof err.message === 'string' && err.message.trim()) {
      const msg = err.message.trim();

      const isValidMessage = msg.length > 0
          && msg.length < 200
          && !msg.toLowerCase().includes('react')
          && !msg.toLowerCase().includes('stack')
          && !msg.toLowerCase().includes('frame')
          && !msg.toLowerCase().includes('received')
          && !msg.toLowerCase().includes('nan')
          && !msg.toLowerCase().includes('invalid_type')
          && !msg.includes('experimental_')
          && !msg.includes('feature flag');

      if (isValidMessage && !isGenericMessage(msg)) {
        messages.push(msg);
      }
      return;
    }

    Object.entries(err).forEach(([key, value]) => {
      if (!key.startsWith('_') && key !== 'ref' && key !== 'type') {
        processError(value, fieldPath ? `${fieldPath}.${key}` : key);
      }
    });
  };

  Object.entries(obj).forEach(([key, value]) => {
    processError(value, key);
  });

  // @ts-ignore
  return [...new Set(messages)];
};

export const FormErrorGroup: React.FC<FormErrorGroupProps> = ({ errors, isSubmitted }) => {
  const { t } = useTranslation();
  const errorMessages = extractMessages(errors);
  const [isVisible, setIsVisible] = React.useState(false);

  const shouldShow = isSubmitted !== undefined ? isSubmitted : true;

  useEffect(() => {
    setIsVisible(shouldShow && errorMessages.length > 0);
  }, [errorMessages.length, shouldShow]);

  if (!shouldShow || errorMessages.length === 0) return null;

  const getErrorTitle = (count: number): string => {
    if (count === 1) {
      return t('formErrors.oneError');
    }
    return t('formErrors.multipleErrors', { count });
  };

  return (
    <ErrorContainer $isVisible={isVisible}>
      <IconWrapper>
        <AlertCircle size={20} />
      </IconWrapper>
      <ContentWrapper>
        <ErrorTitle>
          {getErrorTitle(errorMessages.length)}
        </ErrorTitle>
        <ErrorList>
          {errorMessages.map((msg, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ErrorItem key={index}>{msg}</ErrorItem>
          ))}
        </ErrorList>
      </ContentWrapper>
      <CloseButton
        onClick={() => setIsVisible(false)}
        type="button"
        aria-label={t('common.close')}
      >
        <X size={18} />
      </CloseButton>
    </ErrorContainer>
  );
};
